import { BlobReader, BlobWriter, ZipWriter } from "@zip.js/zip.js";
import { getPdfPageCount } from "./pdf-operations";
import { parsePageRanges } from "./page-ranges";

export interface PdfImageRenderOptions {
  format: "png" | "jpeg";
  dpi: number;
  quality?: number;
  pages?: string;
}

export interface RenderedPdfPage {
  name: string;
  blob: Blob;
}

export function renderScaleForDpi(dpi: number): number {
  if (!Number.isInteger(dpi) || dpi < 72 || dpi > 300) throw new Error("DPI must be an integer between 72 and 300.");
  return dpi / 72;
}

export function validateImageQuality(quality: number): number {
  if (!Number.isFinite(quality) || quality < 0.1 || quality > 1) {
    throw new Error("Image quality must be between 0.1 and 1.");
  }
  return quality;
}

export async function renderPdfToImages(input: Uint8Array, options: PdfImageRenderOptions): Promise<RenderedPdfPage[]> {
  preparePdfJsWorkerEnvironment();
  const pdfjs = await import("pdfjs-dist");
  pdfjs.GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/build/pdf.worker.min.mjs", import.meta.url).toString();
  const pageCount = await getPdfPageCount(input);
  const pages = options.pages ? parsePageRanges(options.pages, pageCount) : Array.from({ length: pageCount }, (_, index) => index + 1);
  const ownerDocument = createWorkerOwnerDocument();
  const document = await pdfjs.getDocument({
    data: input.slice(),
    ...(ownerDocument ? { ownerDocument, disableFontFace: true } : {})
  }).promise;
  const results: RenderedPdfPage[] = [];
  const mime = options.format === "jpeg" ? "image/jpeg" : "image/png";
  const extension = options.format === "jpeg" ? "jpg" : "png";
  const quality = options.format === "jpeg" ? validateImageQuality(options.quality ?? 0.86) : undefined;

  for (const pageNumber of pages) {
    const page = await document.getPage(pageNumber);
    const viewport = page.getViewport({ scale: renderScaleForDpi(options.dpi) });
    const canvas = createCanvas(Math.ceil(viewport.width), Math.ceil(viewport.height));
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    const context = canvas.getContext("2d", { alpha: false });
    if (!context) throw new Error("Canvas rendering is unavailable.");
    await page.render({ canvas: canvas as HTMLCanvasElement, canvasContext: context as CanvasRenderingContext2D, viewport }).promise;
    const blob = await canvasToBlob(canvas, mime, quality);
    results.push({ name: `page-${String(pageNumber).padStart(3, "0")}.${extension}`, blob });
    page.cleanup();
  }
  await document.cleanup();
  return results;
}

function preparePdfJsWorkerEnvironment(): void {
  if (typeof document !== "undefined" || "window" in globalThis) return;
  // PDF.js consults window.location before starting its own parsing Worker.
  // Inside our rendering Worker that throws, selects its fake-worker path and
  // lets the PDF.js protocol take over our parent message channel. Pointing
  // window at the worker global lets PDF.js create the intended nested Worker.
  Object.defineProperty(globalThis, "window", {
    configurable: true,
    value: globalThis
  });
}

function createWorkerOwnerDocument(): Document | undefined {
  if (typeof document !== "undefined" || typeof OffscreenCanvas === "undefined") return undefined;
  const workerGlobal = globalThis as typeof globalThis & { fonts?: FontFaceSet };
  return {
    fonts: workerGlobal.fonts,
    createElement: (name: string) => name === "canvas" ? new OffscreenCanvas(1, 1) : null
  } as unknown as Document;
}

function createCanvas(width: number, height: number): HTMLCanvasElement | OffscreenCanvas {
  if (typeof OffscreenCanvas !== "undefined") return new OffscreenCanvas(width, height);
  if (typeof document === "undefined") throw new Error("PDF image rendering requires Canvas or OffscreenCanvas support.");
  return document.createElement("canvas");
}

function canvasToBlob(canvas: HTMLCanvasElement | OffscreenCanvas, type: string, quality?: number): Promise<Blob> {
  if (typeof OffscreenCanvas !== "undefined" && canvas instanceof OffscreenCanvas) {
    return canvas.convertToBlob({ type, ...(quality === undefined ? {} : { quality }) });
  }
  const htmlCanvas = canvas as HTMLCanvasElement;
  return new Promise<Blob>((resolve, reject) => {
    htmlCanvas.toBlob((value: Blob | null) => value ? resolve(value) : reject(new Error("PDF page rendering failed.")), type, quality);
  });
}

export async function zipRenderedPages(pages: readonly RenderedPdfPage[]): Promise<Blob> {
  const writer = new ZipWriter(new BlobWriter("application/zip"));
  for (const page of pages) await writer.add(page.name, new BlobReader(page.blob));
  return writer.close();
}
